import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Header from '../components/Header'
import Navigation from '../components/Navigation'
import VacationCalculation from '../components/VacationCalculation'
import AllowanceForm from '../components/AllowanceForm'
import CalculationResult from '../components/CalculationResult'
import CityPolicyCard from '../components/CityPolicyCard'
import { AllowanceForm as AllowanceData, HistoryRecord, CalculationResult as ResultData, VacationCalculation as VacationData } from '../types/allowance'
import { CityPolicy, fetchCityPolicy } from '../services/cityPolicyService'
import { post } from "../utils/apiClient";

// 公司垫付信息类型
interface CompanyAdvance {
  fiveInsurancesOneFund?: number;     // 五险一金
  supplementalHousingFund?: number;   // 补充公积金
  laborUnionFee?: number;             // 工会费
  esppAmount?: number;                // ESPP金额
  addItem?: { [key: string]: number }; // 增项
  deleteItem?: { [key: string]: number }; // 减项
}

// 生育津贴请求类型
interface MaternityAllowanceRequest {
  lanId: string;                     // 员工工号
  employeeName: string;              // 员工姓名
  cityCode: string;                  // 城市代码
  maternityLeaveDays: number;        // 享受生育津贴天数
  maternityLeaveStartDate?: string;  // 产假开始日期（从产假计算接口获取）
  maternityLeaveEndDate?: string;    // 产假结束日期（从产假计算接口获取）
  maternityLeaveRequestDate?: string; // 产假提交时间
  averageSalaryPast12Months: number; // 员工产前12个月月均工资
  governmentAllowance: number;       // 政府发放补贴金额
  companyAdvance: number | { addItem: { [key: string]: number }, deleteItem: { [key: string]: number } } | CompanyAdvance;           // 公司垫付的产假工资期间工资
  monthlyBaseSalary: number;        // 月基本工资
  adjustedMonthlyBaseSalary?: number;   // 调整后的月基本工资（跨四月使用）
  unitMonthlyAverageSalary?: number;     // 单位申报的上年度月平均工资（上海使用）
  isMultipleBirth: boolean;          // 是否多胞胎
}

// 生育津贴响应类型
interface MaternityAllowanceResponse {
  lanId: string;                     // 工号
  employeeName: string;              // 姓名
  cityCode: string;                  // 城市代码
  cityName: string;                  // 城市名称
  totalAllowance: number;            // 生育津贴总金额
  dailyAllowance: number;            // 日津贴标准
  allowanceDays: number;             // 享受津贴天数
  calculationBase: number;           // 计算基数
  extraAllowance: number;            // 额外补贴
  maternityAllowance: number;        // 生育津贴金额
  governmentAllowance: number;       // 政府发放补贴
  compensationAmount: number;        // 补差金额
  allowanceCompensationDetails: string | string[];        // 计算说明
  employeeRefundAmount: string;      // 返还金额
  paidMaternityWage: number;         // 产假期间实发工资
  refundDetails?: string | string[]; // 返还金额计算详情
}

const AllowanceCalculator: React.FC = () => {
  const location = useLocation()
  // 产假计算部分默认折叠，从其他页面带入数据时也保持折叠
  const [vacationExpanded, setVacationExpanded] = useState(false)
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [allowanceResult, setAllowanceResult] = useState<MaternityAllowanceResponse | null>(null);
  
  // 安全的数字转换函数，保持精度
  const safeParseNumber = (value: string | undefined): number => {
    if (!value || value === '') return 0
    const num = Number(value)
    return isNaN(num) ? 0 : num
  }

  const [vacationData, setVacationData] = useState<VacationData>({
    employeeName: '',
    employeeId: 'A5',
    city: '',
    startDate: '',
    birthType: 'single',
    hasExtendedDays: true,
    rewardLeave: false,
    deliveryMethod: 'normal',
    calculatedDays: 0
  })

  // Handle data from maternity page

  useEffect(() => {
    const state = location.state as { 
      maternityResult?: { totalDays: number; totalAllowanceDays?: number; isFirstTimeBirth?: boolean }; 
      formData: {
        employeeName: string;
        lanId: string;
        cityCode: string;
        expectedDeliveryDate: string;
        birthType: string;
        hasExtendedDays: boolean;
        rewardLeave: boolean;
        deliveryMethod: string;
        pregnancyDuration?: number;
        maternityLeaveStartDate?: string;
        maternityLeaveEndDate?: string;
        firstChild?: 'yes' | 'no';
      }; 
      allowanceData?: AllowanceData;
      fromHistory?: boolean;
    } | null
    if (state?.maternityResult && state?.formData) {
      const { maternityResult, formData, allowanceData: historyAllowanceData, fromHistory } = state
      
      console.log('从MaternityCalculator接收到的formData:', formData);
      console.log('maternityLeaveStartDate:', formData.maternityLeaveStartDate);
      console.log('maternityLeaveEndDate:', formData.maternityLeaveEndDate);
      
      // Use the original city code from MaternityCalculator directly
      // The VacationCalculation component will fetch cities from API and match by code
      const cityName = formData.cityCode;
      
      const data = {
        employeeName: formData.employeeName,
        employeeId: formData.lanId, // MaternityCalculator uses lanId field
        startDate: formData.expectedDeliveryDate, // MaternityCalculator uses expectedDeliveryDate
        birthType: formData.birthType,
        hasExtendedDays: formData.hasExtendedDays,
        rewardLeave: formData.rewardLeave,
        deliveryMethod: formData.deliveryMethod,
        calculatedDays: maternityResult.totalDays,
        totalAllowanceDays: maternityResult.totalAllowanceDays || maternityResult.totalDays,
        pregnancyDuration: formData.pregnancyDuration,
        firstChild: formData.firstChild, // 绍兴一孩选项
        isFirstTimeBirth: maternityResult.isFirstTimeBirth // 后端返回的一孩结果
      }
      
      setVacationData({
        employeeName: data.employeeName || '',
        employeeId: data.employeeId || '',
        city: cityName,
        startDate: data.startDate,
        birthType: data.birthType as 'single' | 'twins' | 'triplets' | 'quadruplets',
        hasExtendedDays: data.hasExtendedDays,
        rewardLeave: data.rewardLeave,
        deliveryMethod: data.deliveryMethod as 'normal' | 'difficult' | 'miscarriage',
        calculatedDays: data.calculatedDays,
        totalAllowanceDays: data.totalAllowanceDays,
        pregnancyDuration: data.pregnancyDuration,
        maternityLeaveStartDate: formData.maternityLeaveStartDate, // 传递产假开始日期
        maternityLeaveEndDate: formData.maternityLeaveEndDate, // 传递产假结束日期
        firstChild: data.firstChild, // 绍兴一孩选项（用户输入）
        isFirstTimeBirth: data.isFirstTimeBirth // 绍兴一孩结果（后端返回）
      })
      
      // 如果来自历史记录，恢复津贴计算数据
      if (fromHistory && historyAllowanceData) {
        const nonPayrollCities = ['TJ', 'SX', 'XM', 'SH', 'QD']
        
        setAllowanceData({
          city: cityName,
          governmentAmount: (historyAllowanceData.governmentAmount || 0).toString(),
          advanceInputMethod: historyAllowanceData.advanceInputMethod || 'detailed',
          advanceTotal: (historyAllowanceData.advanceTotal || 0).toString(),
          employeeInsurance: (historyAllowanceData.employeeInsurance || 0).toString(),
          supplementaryFund: (historyAllowanceData.supplementaryFund || 0).toString(),
          unionFee: (historyAllowanceData.unionFee || 0).toString(),
          espp: (historyAllowanceData.espp || 0).toString(),
          socialInsuranceBase: (historyAllowanceData.socialInsuranceBase || 0).toString(),
          employerESPP: (historyAllowanceData.employerESPP || 0).toString(),
          employerSupplementaryFund: (historyAllowanceData.employerSupplementaryFund || 0).toString(),
          personalIncomeTax: (historyAllowanceData.personalIncomeTax || 0).toString(),
          otherDeductions: (historyAllowanceData.otherDeductions || 0).toString(),
          flexibleBenefit: (historyAllowanceData.flexibleBenefit || 0).toString(),
          spotOn: (historyAllowanceData.spotOn || 0).toString(),
          customInputs: historyAllowanceData.customInputs || [],
          customDeductions: historyAllowanceData.customDeductions || [],
          paidSalary: (historyAllowanceData.paidSalary || 0).toString(),
          declaredSalary: (historyAllowanceData.declaredSalary || 0).toString(),
          avgSalary12Months: (historyAllowanceData.avgSalary12Months || 0).toString(),
          crossJuly: historyAllowanceData.crossJuly || false,
          adjustedSocialInsuranceBase: (historyAllowanceData.adjustedSocialInsuranceBase || 0).toString(),
          crossApril: historyAllowanceData.crossApril || false,
          monthlyBaseSalary: (historyAllowanceData.monthlyBaseSalary || 0).toString(),
          adjustedMonthlyBaseSalary: (historyAllowanceData.adjustedMonthlyBaseSalary || 0).toString(),
          paidSalaryBeforeApril: (historyAllowanceData.paidSalaryBeforeApril || 0).toString(),
          paidSalaryAfterApril: (historyAllowanceData.paidSalaryAfterApril || 0).toString(),
          isBankEmployee: historyAllowanceData.isBankEmployee || false,
          receivedTime: historyAllowanceData.receivedTime || undefined,
          submissionTime: historyAllowanceData.submissionTime || undefined,
          // 恢复产假提交时间，如果没有则使用产假开始日期作为默认值
          maternitySubmissionDate: historyAllowanceData.maternitySubmissionDate || 
            (nonPayrollCities.includes(cityName) ? data.startDate : undefined)
        })
      } else {
        // 新计算时更新城市并设置默认时间
        const now = new Date()
        const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        const nonPayrollCities = ['TJ', 'SX', 'XM', 'SH', 'QD']
        
        setAllowanceData(prev => ({ 
          ...prev, 
          city: cityName,
          receivedTime: currentDate, // 设置提交核定表时间默认值
          // 设置产假提交时间默认值（特定城市）
          maternitySubmissionDate: nonPayrollCities.includes(cityName) 
            ? (data.startDate || currentDate) 
            : prev.maternitySubmissionDate
        }))
      }
      
      // Load city policy immediately after setting vacation data
      if (cityName) {
        loadCityPolicy(cityName)
      }
      
      // Keep vacation section collapsed when coming from maternity page
      setVacationExpanded(false)
    }
  }, [location.state])
  
  const [allowanceData, setAllowanceData] = useState<AllowanceData>(() => {
    // 获取当前日期作为默认值
    const now = new Date()
    const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    
    return {
      city: '',
      advanceInputMethod: 'detailed',
      crossJuly: false,
      crossApril: false,
      isBankEmployee: false,
      receivedTime: currentDate, // 默认为当前日期
      submissionTime: undefined // 初始化为undefined，根据城市类型动态设置
    }
  })
  
  const [result, setResult] = useState<ResultData | null>(null)
  const [cityPolicy, setCityPolicy] = useState<CityPolicy | null>(null)

  // Load city policy
  const loadCityPolicy = async (cityCode: string) => {
    if (cityCode) {
      try {
        // Use the enhanced getCityPolicy from cityPolicyService which handles all mappings
        const policy = await fetchCityPolicy(cityCode)
        setCityPolicy(policy)
      } catch (error) {
        console.error('Failed to load city policy:', error)
        setCityPolicy(null)
      }
    } else {
      setCityPolicy(null)
    }
  }

  // Create initial allowance data with only city preserved
  const createInitialAllowanceData = (cityCode: string): AllowanceData => {
    return {
      city: cityCode,
      governmentAmount: undefined,
      advanceInputMethod: 'detailed',
      advanceTotal: undefined,
      employeeInsurance: undefined,
      supplementaryFund: undefined,
      unionFee: undefined,
      espp: undefined,
      socialInsuranceBase: undefined,
      employerSupplementaryFund: undefined,
      personalIncomeTax: undefined,
      otherDeductions: undefined,
      paidSalary: undefined,
      declaredSalary: undefined,
      avgSalary12Months: undefined,
      crossJuly: false,
      adjustedSocialInsuranceBase: undefined,
      crossApril: false,
      monthlyBaseSalary: undefined,
      adjustedMonthlyBaseSalary: undefined,
      paidSalaryBeforeApril: undefined,
      paidSalaryAfterApril: undefined,
      isBankEmployee: false,
      receivedTime: undefined, // 根据城市类型动态设置
      submissionTime: undefined // 根据城市类型动态设置
    }
  }

  // Check if critical vacation fields have changed
  const hasCriticalFieldsChanged = (newData: VacationData, oldData: VacationData): boolean => {
    const criticalFields: (keyof VacationData)[] = [
      'city', 'startDate', 'deliveryMethod', 'birthType', 'hasExtendedDays', 'pregnancyDuration'
    ]
    
    return criticalFields.some(field => newData[field] !== oldData[field])
  }

  // Sync city selection between components and clear allowance data when critical fields change
  const handleVacationChange = (data: VacationData) => {
    const shouldClearAllowanceData = hasCriticalFieldsChanged(data, vacationData)
    
    setVacationData(data)
    
    if (shouldClearAllowanceData) {
      // Clear allowance form data and results when critical vacation fields change
      setAllowanceData(createInitialAllowanceData(data.city))
      setResult(null)
      setAllowanceResult(null)
      setErrorMessage('')
      setLoading(false)
      
      // Load city policy for the new city
      if (data.city) {
        loadCityPolicy(data.city)
      }
    } else if (data.city !== allowanceData.city) {
      // Only sync city if no critical fields changed
      setAllowanceData(prev => ({ ...prev, city: data.city }))
      // Load city policy when city changes
      loadCityPolicy(data.city)
    }
  }

  const handleAllowanceChange = (data: AllowanceData) => {
    // 获取当前日期
    const now = new Date()
    const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    
    // 设置默认值
    const updatedData = { ...data }
    
    // 设置提交核定表时间默认值（所有城市）
    if (data.city && !updatedData.receivedTime) {
      updatedData.receivedTime = currentDate
    }
    
    // 设置产假提交时间默认值（特定城市）
    const nonPayrollCities = ['TJ', 'SX', 'XM', 'SH', 'QD']
    if (nonPayrollCities.includes(data.city) && !updatedData.maternitySubmissionDate) {
      // 优先使用产假开始日期，否则使用当前日期
      updatedData.maternitySubmissionDate = vacationData.startDate || currentDate
    }
    
    setAllowanceData(updatedData)
    if (data.city !== vacationData.city) {
      setVacationData(prev => ({ ...prev, city: data.city }))
      // Load city policy when city changes
      loadCityPolicy(data.city)
    }
  }

  const handleCalculate = async () => {
    const allowanceDays = vacationData.totalAllowanceDays || vacationData.calculatedDays;
    if (!vacationData.employeeName || !vacationData.employeeId || !vacationData.city || !allowanceDays) {
      setErrorMessage("请小主先完善假期数据");
      return;
    }

    if (!allowanceData.avgSalary12Months || !allowanceData.governmentAmount) {
      setErrorMessage("请小主完善津贴计算基础信息");
      return;
    }

    // 验证Base Salary字段（当显示时为必填项）
    if (!allowanceData.crossApril && !allowanceData.paidSalary) {
      setErrorMessage("请小主填写员工的基本工资");
      return;
    }

    // 验证跨四月时的调整前和调整后基本工资字段（必填且大于0）
    if (allowanceData.crossApril) {
      if (!allowanceData.monthlyBaseSalary || parseFloat(allowanceData.monthlyBaseSalary) <= 0) {
        setErrorMessage("跨四月时，请填写调整前的基本工资");
        return;
      }
      if (!allowanceData.adjustedMonthlyBaseSalary || parseFloat(allowanceData.adjustedMonthlyBaseSalary) <= 0) {
        setErrorMessage("跨四月时，请填写调整后的基本工资");
        return;
      }
    }

    setLoading(true);
    setErrorMessage("");

    try {      
      // 构建请求数据
      const requestData: MaternityAllowanceRequest = {
        lanId: vacationData.employeeId,
        employeeName: vacationData.employeeName,
        cityCode: vacationData.city || "",
        maternityLeaveDays: vacationData.totalAllowanceDays || vacationData.calculatedDays,
        ...(vacationData.maternityLeaveStartDate && { maternityLeaveStartDate: vacationData.maternityLeaveStartDate }), // 只有当值存在时才添加
        ...(vacationData.maternityLeaveEndDate && { maternityLeaveEndDate: vacationData.maternityLeaveEndDate }), // 只有当值存在时才添加
        ...(allowanceData.maternitySubmissionDate && { maternityLeaveRequestDate: allowanceData.maternitySubmissionDate }), // 产假提交时间
        averageSalaryPast12Months: safeParseNumber(allowanceData.avgSalary12Months || '0'),
        governmentAllowance: safeParseNumber(allowanceData.governmentAmount || '0'),
        companyAdvance: allowanceData.advanceInputMethod === "total" ? safeParseNumber(allowanceData.advanceTotal || '0') : 
          allowanceData.advanceInputMethod === "detailed" ? (() => {
            // 构建增项对象
            const addItem: { [key: string]: number } = {};
            
            // 添加社保缴费基数到增项中
            const shouldShowCrossJuly = () => {
              if (!vacationData?.startDate || !vacationData?.calculatedDays) {
                return false
              }
              
              const startDate = new Date(vacationData.startDate)
              const endDate = new Date(startDate)
              endDate.setDate(startDate.getDate() + vacationData.calculatedDays)
              
              // 检查产假期间是否包含任何年份的7月份
              const startYear = startDate.getFullYear()
              const endYear = endDate.getFullYear()
              
              // 遍历产假期间涉及的所有年份，检查是否包含7月份
              for (let year = startYear; year <= endYear; year++) {
                const julyStart = new Date(year, 6, 1) // 7月1日
                const julyEnd = new Date(year, 6, 31)   // 7月31日
                
                // 检查产假期间是否与该年的7月份有重叠
                if (startDate <= julyEnd && endDate >= julyStart) {
                  return true
                }
              }
              
              return false
            }
            
            if (shouldShowCrossJuly()) {
              // 跨七月情况：使用调整前的社保基数
              const socialInsuranceValue = safeParseNumber(allowanceData.socialInsuranceBase || '0');
              if (socialInsuranceValue > 0) {
                addItem['socialInsuranceBase'] = socialInsuranceValue;
              }
              // 添加调整后的社保基数
              const adjustedSocialInsuranceValue = safeParseNumber(allowanceData.adjustedSocialInsuranceBase || '0');
              if (adjustedSocialInsuranceValue > 0) {
                addItem['adjustedSocialInsuranceBase'] = adjustedSocialInsuranceValue;
              }
            } else {
              // 非跨七月情况：使用员工保险字段
              const socialInsuranceValue = safeParseNumber(allowanceData.employeeInsurance || '0');
              if (socialInsuranceValue > 0) {
                addItem['socialInsuranceBase'] = socialInsuranceValue;
              }
            }
            
            const defaultItems = [
              { key: 'socialInsuranceBase', label: 'socialInsuranceBase' },
              { key: 'employerESPP', label: 'ESPP' },
              { key: 'personalIncomeTax', label: 'unionFee' },
              { key: 'flexibleBenefit', label: 'flexibleBenefit' }
            ];
            
            defaultItems.forEach(item => {
              const value = safeParseNumber(allowanceData[item.key as keyof typeof allowanceData] as string || '0');
              if (value > 0) {
                addItem[item.label] = value;
              }
            });
            
            (allowanceData.customInputs || []).forEach(customItem => {
              const value = safeParseNumber(customItem.value || '0');
              if (value > 0) {
                addItem[customItem.label] = value;
              }
            });
            
            // 构建减项对象
            const deleteItem: { [key: string]: number } = {};
            const spotOnValue = safeParseNumber(allowanceData.spotOn as string || '0');
            if (spotOnValue > 0) {
              deleteItem['spot on'] = spotOnValue;
            }
            
            (allowanceData.customDeductions || []).forEach(deductionItem => {
              const value = safeParseNumber(deductionItem.value || '0');
              if (value > 0) {
                deleteItem[deductionItem.label] = value;
              }
            });
            
            return { addItem, deleteItem };
          })() : allowanceData.advanceInputMethod === "itemized" ? {
            fiveInsurancesOneFund: safeParseNumber(allowanceData.employeeInsurance || '0'),
            supplementalHousingFund: safeParseNumber(allowanceData.supplementaryFund || '0'),
            laborUnionFee: safeParseNumber(allowanceData.unionFee || '0'),
            esppAmount: safeParseNumber(allowanceData.espp || '0'),
          } : 0,
        monthlyBaseSalary: allowanceData.crossApril 
          ? safeParseNumber(allowanceData.monthlyBaseSalary || '0')
          : safeParseNumber(allowanceData.paidSalary || '0'),
        adjustedMonthlyBaseSalary: allowanceData.crossApril 
          ? safeParseNumber(allowanceData.adjustedMonthlyBaseSalary || '0')
          : undefined,
        unitMonthlyAverageSalary: vacationData.city === "SH" ? safeParseNumber(allowanceData.declaredSalary || '0') : undefined,
        isMultipleBirth: vacationData.birthType !== "single", // 单胎为false，双胞胎、三胞胎、四胞胎为true
      };
      // 调用API
      const response = await post<MaternityAllowanceResponse>(
        "https://www.54cpa.cn/api/maternity-allowance/calculate",
        requestData
      );

      setAllowanceResult(response);
      
      // 调试：打印返还金额数据
      console.log('🔍 API返回的employeeRefundAmount:', response.employeeRefundAmount, typeof response.employeeRefundAmount);

      // 同时保留原有的简单计算结果用于显示
      const calculationResult: ResultData = {
        allowanceAmount: response.maternityAllowance || 0,
        governmentAmount: allowanceData.governmentAmount ? parseFloat(allowanceData.governmentAmount) : 0,
        paidSalary: typeof requestData.companyAdvance === 'number' ? requestData.companyAdvance : 0,
        difference: response.compensationAmount || 0,
        futureAdjustment: 0,
        breakdown: [
          `产假天数: ${response.allowanceDays || 0}天`,
          `津贴总金额: ¥${(response.totalAllowance || 0).toLocaleString()}`,
          `津贴补差金额: ¥${(response.compensationAmount || 0).toLocaleString()}`,
          `返还金额: ¥${(response.employeeRefundAmount || 0).toLocaleString()}`
        ],
        recommendation: Array.isArray(response.allowanceCompensationDetails) 
          ? response.allowanceCompensationDetails.join('\n') 
          : response.allowanceCompensationDetails || "计算完成",
      };
      setResult(calculationResult);

      // 保存到历史记录
      saveToHistory(calculationResult, response);

    } catch (error: unknown) {
      console.error("计算津贴失败:", error);
      const message = typeof error === "object" && error && "message" in error
        ? String((error as { message?: unknown }).message)
        : "计算失败，请稍后重试";
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };


  const saveToHistory = (result: ResultData, apiResponse?: MaternityAllowanceResponse) => {
    if (!vacationData.employeeName || !vacationData.employeeId) {
      return // 如果没有员工信息，不保存历史记录
    }

    // 使用传入的API响应数据，避免状态更新延迟问题
    const refundAmount = apiResponse?.employeeRefundAmount || allowanceResult?.employeeRefundAmount;
    console.log('💾 保存历史记录时的返还金额:', refundAmount, typeof refundAmount);

    console.log('💾 保存历史记录 - allowanceData.receivedTime:', allowanceData.receivedTime)
    console.log('💾 保存历史记录 - allowanceData.submissionTime:', allowanceData.submissionTime)
    console.log('💾 保存历史记录 - allowanceData.maternitySubmissionDate:', allowanceData.maternitySubmissionDate)
    
    const historyRecord = {
      id: `${vacationData.employeeId}_${Date.now()}`,
      employeeName: vacationData.employeeName,
      employeeId: vacationData.employeeId,
      city: vacationData.city,
      cityName: getCityDisplayName(vacationData.city),
      startDate: vacationData.startDate,
      endDate: calculateEndDate(vacationData.startDate, vacationData.calculatedDays || 0),
      totalDays: vacationData.calculatedDays || 0,
      companyCompensation: result.difference,
      employeeCompensation: typeof refundAmount === 'string' 
        ? parseFloat(refundAmount) 
        : (refundAmount || 0),
      calculatedAt: new Date().toISOString(),
      recordType: 'allowance' as const, // 标记为津贴计算记录
      receivedTime: allowanceData.receivedTime, // 提交核定表时间
      submissionTime: allowanceData.submissionTime, // 提交产假时间
      vacationData: vacationData,
      allowanceData: {
        ...allowanceData,
        customInputs: allowanceData.customInputs || [],
        customDeductions: allowanceData.customDeductions || [],
        // 确保保存政府津贴和应享受津贴数据
        governmentSubsidy: allowanceData.governmentAmount ? parseFloat(allowanceData.governmentAmount) : 0,
        monthlyAverageSalary: result.allowanceAmount || 0, // 应享受津贴金额使用result.allowanceAmount
        socialInsuranceBase: allowanceData.socialInsuranceBase ? parseFloat(allowanceData.socialInsuranceBase) : 0, // 修正：使用正确的socialInsuranceBase字段
        employeeInsurance: allowanceData.employeeInsurance ? parseFloat(allowanceData.employeeInsurance) : 0, // 新增：保存员工保险字段
        // 新增：保存产假期间实发工资（paidMaternityWage）
        paidMaternityWage: typeof apiResponse?.paidMaternityWage === 'number'
          ? apiResponse.paidMaternityWage
          : (allowanceResult?.paidMaternityWage || 0),
        // 明确保存产假提交时间
        maternitySubmissionDate: allowanceData.maternitySubmissionDate || undefined
      } as unknown as { [key: string]: unknown }
    }

    console.log('💾 最终保存的历史记录:', {
      receivedTime: historyRecord.receivedTime,
      submissionTime: historyRecord.submissionTime,
      maternitySubmissionDate: historyRecord.allowanceData.maternitySubmissionDate
    })
    
    saveToHistoryStorage(historyRecord)
  }

  const getCityDisplayName = (cityCode: string) => {
    const cityMap: { [key: string]: string } = {
      // 小写映射
      'beijing': '北京',
      'foshan': '佛山',
      'guangzhou': '广州',
      'shenzhen': '深圳',
      'zhuhai': '珠海',
      'wuhan': '武汉',
      'suzhou': '苏州（园区）',
      'chengdu': '成都',
      'chongqing': '重庆',
      'tianjin': '天津',
      'xiamen': '厦门',
      'shanghai': '上海',
      'qingdao': '青岛',
      'shaoxing': '绍兴',
      // 大写映射
      'BJ': '北京',
      'FS': '佛山',
      'GZ': '广州',
      'SZ': '深圳',
      'ZH': '珠海',
      'WH': '武汉',
      'SU': '苏州（园区）',
      'CD': '成都',
      'CQ': '重庆',
      'TJ': '天津',
      'XM': '厦门',
      'SH': '上海',
      'QD': '青岛',
      'SX': '绍兴'
    }
    return cityMap[cityCode] || cityCode
  }

  const saveToHistoryStorage = (record: HistoryRecord) => {
    try {
      console.log('🔄 准备保存历史记录:', record)
      console.log('🔍 allowanceData内容:', record.allowanceData)
      console.log('🔍 maternitySubmissionDate值:', record.allowanceData?.maternitySubmissionDate)
      
      const existingRecords = JSON.parse(localStorage.getItem('hr_maternity_history') || '[]')
      console.log('📋 现有记录数量:', existingRecords.length)
      
      // 移除同一工号的旧记录
      const filteredRecords = existingRecords.filter((r: HistoryRecord) => r.employeeId !== record.employeeId)
      console.log('🗑️ 过滤后记录数量:', filteredRecords.length)
      
      // 添加新记录到开头（最新的在前面）
      const updatedRecords = [record, ...filteredRecords]
      console.log('✅ 更新后记录数量:', updatedRecords.length)
      
      localStorage.setItem('hr_maternity_history', JSON.stringify(updatedRecords))
      console.log('💾 历史记录已保存到localStorage')
      
      // 验证保存是否成功
      const savedData = localStorage.getItem('hr_maternity_history')
      if (savedData) {
        const parsedData = JSON.parse(savedData)
        console.log('✅ 验证保存成功，记录数量:', parsedData.length)
        console.log('🔍 验证第一条记录的maternitySubmissionDate:', parsedData[0]?.allowanceData?.maternitySubmissionDate)
      }
    } catch (error) {
      console.error('❌ 保存历史记录失败:', error)
    }
  }

  const calculateEndDate = (startDate: string, totalDays: number) => {
    const start = new Date(startDate)
    const end = new Date(start)
    end.setDate(start.getDate() + totalDays)
    return end.toISOString().split('T')[0]
  }

  return (
    <div>
      <Header />
      <Navigation />
      
      <div className="container">
        <header className="header">
          <div className="header-content">
            <i className="fas fa-money-bill-wave header-icon"></i>
            <h1>生育津贴补差计算器</h1>
            <p className="subtitle">准确计算生育津贴补差金额，确保员工权益保障</p>
          </div>
        </header>

        <main className="main-content">
          <div className="allowance-layout">
            <div className="calculator-section">
              <div className="calculator-card">
                <div className="card-header">
                  <h2><i className="fas fa-calculator"></i> 补差计算</h2>
                </div>
                
                <form className="calculator-form">
                  <VacationCalculation
                    data={vacationData}
                    onChange={handleVacationChange}
                    expanded={vacationExpanded}
                    onToggle={() => setVacationExpanded(!vacationExpanded)}
                  />
                  
                  <AllowanceForm
                    data={allowanceData}
                    vacationData={vacationData}
                    onChange={handleAllowanceChange}
                    onSubmit={handleCalculate}
                    loading={loading}
                    errorMessage={errorMessage}
                    cityPolicy={cityPolicy}
                  />
                </form>
                
                <CalculationResult 
                  result={result} 
                  allowanceResult={allowanceResult}
                  formData={allowanceData}
                  vacationData={vacationData}
                />
                
                {/* 城市政策说明 */}
                {allowanceResult?.allowanceCompensationDetails && (
                  <div className="city-policy-section">
                    <CityPolicyCard 
                      policy={cityPolicy} 
                      allowanceCompensationDetails={allowanceResult?.allowanceCompensationDetails}
                      refundDetails={allowanceResult?.refundDetails}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="info-section">
              <div className="info-card">
                <div className="card-header">
                  <h3><i className="fas fa-info-circle"></i> 补差政策说明</h3>
                </div>
                <div className="info-content">
                  <ul className="policy-list">
                    <li><strong>计算原则：</strong>生育津贴应按产前12个月平均工资计算</li>
                    <li><strong>补差条件：</strong>当计算出的津贴高于政府发放金额时需要补差</li>
                    <li><strong>补差责任：</strong>用人单位负责补足差额部分</li>
                    <li><strong>发放时间：</strong>应在收到政府津贴后及时补差</li>
                    <li><strong>税务处理：</strong>补差部分按工资薪金所得计税</li>
                    <li><strong>记录保存：</strong>相关计算和发放记录应妥善保存</li>
                  </ul>
                  
                  <div className="formula-section">
                    <h4><i className="fas fa-calculator"></i> 津贴计算公式</h4>
                    
                    {/* 产假期间发工资的城市 - 简化公式 */}
                    {cityPolicy && cityPolicy.cityName.includes('深圳') ? (
                      <>
                        <div className="formula-step">
                          <h5>第一步：计算各项基数</h5>
                          <div className="formula-box">
                            <p><strong>A = 产假期间领到工资总数</strong></p>
                            <p><strong>B = 员工产前12个月月均工资 ÷ 30 × 享受生育津贴天数</strong></p>
                            <p><strong>C = 政府发放补贴金额</strong></p>
                          </div>
                        </div>

                        <div className="formula-step">
                          <h5>第二步：确定员工应享受补贴</h5>
                          <div className="formula-box other-cities-formula">
                              <p><strong>D = MAX(B, C)</strong></p>
                            </div>
                        </div>

                        <div className="formula-step">
                          <h5>第三步：计算补差结果</h5>
                          <div className="formula-box result-formula">
                            <p><strong>补差金额 = D - A</strong></p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* 产假期间不发工资的城市 */}
                        <div className="formula-step">
                          <h5>第一步：计算各项基数</h5>
                          <div className="formula-box">
                            {/* 上海特有的单位申报工资项 */}
                            {vacationData.city === 'SH' && (
                              <>
                                <p><strong>A = 单位申报的上年度月平均工资 ÷ 30 × 享受生育津贴天数</strong></p>
                              </>
                            )}
                            <p><strong>B = 员工产前12个月月均工资 ÷ 30 × 享受生育津贴天数</strong></p>
                            <p><strong>C = 政府发放补贴金额</strong></p>
                          </div>
                        </div>

                        <div className="formula-step">
                          <h5>第二步：确定员工应享受补贴</h5>
                          {/* 上海地区公式 */}
                          {vacationData.city === 'SH' && (
                            <div className="formula-box shanghai-formula">
                              <p><strong>D = MAX(A, B, C)</strong></p>
                            </div>
                          )}
                          {/* 其他城市公式 */}
                          {vacationData.city !== 'SH' && (
                            <div className="formula-box other-cities-formula">
                              <p><strong>D = MAX(B, C)</strong></p>
                            </div>
                          )}
                        </div>

                        <div className="formula-step">
                          <h5>第三步：计算补差结果</h5>
                          <div className="formula-box result-formula">
                            <p><strong>补差金额 = D - C</strong></p>
                          </div>
                        </div>
                      </>
                    )}

                    {['TJ', 'SX', 'XM', 'SH', 'QD'].includes(vacationData.city) && (
                      <div className="formula-step">
                        <h5>第四步：返工时需返还金额</h5>
                        <div className="formula-box return-formula">
                          <p><strong>返还金额 = 员工五险一金 + 补充公积金{vacationData.city === 'SH' ? '（仅上海）' : ''} + 个人工会费 + ESPP</strong></p>
                          <p className="formula-note">适用城市：天津、绍兴、厦门、上海、青岛</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="footer">
          <p> 2025 Twins HR产假津贴管理系统 | 仅供内部使用</p>
        </footer>
      </div>
    </div>
  )
}

export default AllowanceCalculator
